<template>
	<div
		class="amelipro-card w-100 d-flex flex-column flex-md-row"
		:class="classes"
		:style="generalCardStyles"
	>
		<div
			class="amelipro-card__left-part"
			:style="leftPartWidth"
		>
			<div
				v-if="!noCardHeader"
				class="amelipro-card__header w-100"
				:class="{
					'amelipro-card__header--left': rightPart,
				}"
			>
				<div
					class="w-100 d-flex flex-column flex-sm-row align-sm-center amelipro-card__header__content"
					:style="cardHeaderStyles"
				>
					<div
						class="amelipro-card__header__slot-wrapper--left"
						:style="headerLeftStyles"
					>
						<slot name="headerLeft">
							<p
								v-if="cardTitle"
								:aria-level="titleLevel"
								class="text-h2 mb-0 amelipro-card__title"
								role="heading"
								:style="cardTitleStyle"
							>
								{{ cardTitle }}
							</p>
						</slot>
					</div>

					<div
						v-if="hasRightSlot"
						class="amelipro-card__header__slot-wrapper--right"
						:style="headerRightStyles"
					>
						<slot name="headerRight" />
					</div>
				</div>
			</div>

			<slot name="fullSizeImg" />

			<div
				class="amelipro-card__content"
				:class="computedContentClass"
			>
				<slot />
			</div>
		</div>

		<div
			v-if="rightPart"
			class="amelipro-card__right-part"
			:style="rightPartStyle"
		>
			<div
				class="w-100 amelipro-card__right-part__content"
				:class="rightPartClasses"
			>
				<slot name="rightPartContent" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, useSlots } from 'vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { IndexedObject } from '../../types';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		borderColor: {
			type: String,
			default: 'ap-grey lighten-2',
		},
		bordered: {
			type: Boolean,
			default: true,
		},
		cardColor: {
			type: String,
			default: 'ap-white',
		},
		cardTitle: {
			type: String,
			default: undefined,
		},
		classes: {
			type: String,
			default: undefined,
		},
		contentClasses: {
			type: String,
			default: undefined,
		},
		divider: {
			type: Boolean,
			default: true,
		},
		headerRightWidth: {
			type: String,
			default: '50%',
		},
		noCardHeader: {
			type: Boolean,
			default: false,
		},
		rightPart: {
			type: Boolean,
			default: false,
		},
		rightPartClasses: {
			type: String,
			default: undefined,
		},
		rightPartWidth: {
			type: String,
			default: '25%',
		},
		titleColor: {
			type: String,
			default: 'ap-grey darken-1',
		},
		titleLevel: {
			type: Number,
			default: 2,
		},
	});

	const computedContentClass = computed(() => {
		const classes = { 'amelipro-card__content--left': props.rightPart } as { [key: string]: boolean };
		if (props.contentClasses) {
			props.contentClasses.split(' ').forEach((className: string) => {
				classes[className] = true;
			});
		}
		return classes;
	});

	const breakpointHelper = new BreakpointHelper();
	const slots = useSlots();
	const cardTitleStyle = computed(() => ({ color: convertToHex(props.titleColor) }));
	const borderColorValue = computed(() => convertToHex(props.borderColor));

	const generalCardStyles = computed(() => {
		const cardStyle: IndexedObject = { backgroundColor: `${convertToHex(props.cardColor)}` };

		if (props.bordered) {
			cardStyle.border = `1px solid ${borderColorValue.value}`;
		}

		return cardStyle;
	});

	const cardHeaderStyles = computed(() => {
		if (props.divider && !props.noCardHeader) {
			return { borderBottom: `1px solid ${borderColorValue.value}` };
		}
		return undefined;
	});

	const hasRightSlot = computed(() => Boolean(slots.headerRight));

	const headerLeftStyles = computed(() => {
		if (hasRightSlot.value && breakpointHelper) {
			return {
				width: `calc( 100% - (${props.headerRightWidth} + 10px))`,
				marginRight: '10px',
			};
		}
		return { width: '100%' };
	});

	const headerRightStyles = computed(() => {
		if (hasRightSlot.value && breakpointHelper) {
			return {
				width: `calc(${props.headerRightWidth} - 10px)`,
				marginLeft: '10px',
			};
		}
		return { width: '100%' };
	});

	const rightPartStyle = computed(() => {
		if (breakpointHelper.isDesktop()) {
			return {
				maxWidth: props.rightPartWidth,
				minWidth: props.rightPartWidth,
				width: props.rightPartWidth,
			};
		}
		return { width: '100%' };
	});

	const leftPartWidth = computed(() => {
		if (props.rightPart && breakpointHelper.isDesktop()) {
			return {
				maxWidth: `calc( 100% - ${props.rightPartWidth} )`,
				minWidth: `calc( 100% - ${props.rightPartWidth} )`,
				width: `calc( 100% - ${props.rightPartWidth} )`,
			};
		}

		return { width: '100%' };
	});
</script>

<style lang="scss" scoped>
.amelipro-card {
	display: block;
	position: relative;
	max-width: 100%;
	border-radius: $card-radius;
	text-decoration: none;
	overflow-wrap: break-word;
	white-space: normal;
}

.amelipro-card__header {
	padding: $card-padding--desktop;
	word-break: break-all;

	@media #{$media-down-md} {
		padding: $card-padding--tablet;
	}

	@media #{$media-down-sm} {
		padding: $card-padding--mobile;
	}

	padding-bottom: 0 !important;
}

.amelipro-card__header__content {
	padding-bottom: 1rem;
}

.amelipro-card__content {
	width: 100%;
	padding: $card-padding--desktop;
	font-size: $font-size-xs;
	font-weight: $ap-font-weight-regular;

	@media #{$media-down-md} {
		padding: $card-padding--tablet;
	}

	@media #{$media-down-sm} {
		padding: $card-padding--mobile;
	}

	padding-top: $card-padding--desktop;
}

.amelipro-card__header--left,
.amelipro-card__content--left {
	@media #{$media-up-md} {
		padding-right: 1rem;
	}
}
.amelipro-card__header__slot-wrapper--left {
	display: inline-block;
}

.amelipro-card__right-part {
	padding: $card-padding--desktop $card-padding--desktop $card-padding--desktop 0;

	@media #{$media-down-md} {
		padding: $card-padding--tablet;
		padding-top: 0;
	}

	@media #{$media-down-sm} {
		padding: $card-padding--mobile;
		padding-top: 0;
	}
}

.amelipro-card__right-part__content {
	@media #{$media-up-md} {
		padding-left: 1rem;
		min-height: 100%;
		border-left: 1px solid v-bind(borderColorValue);
	}

	@media #{$media-down-md} {
		padding-top: $card-padding--tablet;
		border-top: 1px solid v-bind(borderColorValue);
	}

	@media #{$media-down-sm} {
		padding-top: $card-padding--mobile;
	}
}
</style>
